import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import "./StudentProgress.css";
import NavbarAll from "../Navbar/NavbarAll";
import DayDetails from "./DayDetail";
function getInitial(nameOrEmail) {
  const s = (nameOrEmail || "?").trim();
  return s.charAt(0).toUpperCase();
}

export default function StudentProgress({ user: hifzUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const paramsRoute = useParams();
  const pathname = location.pathname || "";
  const urlStudentId = paramsRoute.studentId || paramsRoute.id;
  const isOnStudentPortalRoute = pathname.startsWith("/dashboard-student");
  const isOnQariRoute = pathname.startsWith("/dashboard-qari/students");

  const [student, setStudent] = useState(location.state?.student || null);
  const storedRole = localStorage.getItem("role");
  const role = isOnStudentPortalRoute
    ? location.state?.role || "student"
    : isOnQariRoute
      ? location.state?.role || "teacher"
      : location.state?.role || hifzUser?.role || storedRole || "student";
  const [studentId, setStudentId] = useState(() => {
    const fromUrl = urlStudentId;
    const fromState = location.state?.student?.uid || location.state?.student?.id;
    const fromStorage = localStorage.getItem("userId");
    const fromUser = hifzUser?.userId;
    return fromUrl || fromState || fromUser || fromStorage || "";
  });

  useEffect(() => {
    if (urlStudentId) setStudentId(urlStudentId);
  }, [urlStudentId]);

  useEffect(() => {
    if (studentId) return;
    const id = hifzUser?.userId || localStorage.getItem("userId");
    if (id) setStudentId(id);
  }, [studentId, hifzUser?.userId]);

  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [days, setDays] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolvingStudent, setResolvingStudent] = useState(false);
  const [feesStatus, setFeesStatus] = useState("");
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const apiURL = process.env.REACT_APP_API_URL;
  const [updatingFees, setUpdatingFees] = useState(false);

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const currentMonthName = monthNames[currentMonth];

  const params = new URLSearchParams(location.search);
  const deepLinkToken = params.get("token");

  const isTeacherView =
    isOnQariRoute || role === "teacher" || role === "qari";
  const isAdminView = role === "admin";
  const isStaffViewingStudent = isOnQariRoute && Boolean(urlStudentId);
  const studentDisplayName = student?.name || "Student";

  const backLabel =
    isTeacherView || (isStaffViewingStudent && storedRole === "qari")
      ? "← My Class"
      : isAdminView || storedRole === "admin"
        ? "← Admin"
        : "← Dashboard";

  const goToProfile = () => {
    const id = student?.uid || student?.id || studentId;
    if (isOnQariRoute && id) {
      navigate(`/dashboard-qari/students/${id}/profile`, {
        state: { student, from: "progress" },
      });
      return;
    }
    const search = location.search;
    navigate(
      {
        pathname: "/dashboard-student/profile",
        search,
      },
      {
        state: student
          ? { student, role: "student", from: "progress" }
          : { role: "student", from: "progress" },
      }
    );
  };

  const goToDashboard = () => {
    navigate("/dashboard-student", {
      state: { ...location.state, student },
      search: location.search,
    });
  };

  const goBack = () => {
    if (deepLinkToken) {
      navigate({ pathname: "/dashboard-student", search: location.search });
      return;
    }
    if (isAdminView || storedRole === "admin") {
      navigate("/dashboard-admin");
      return;
    }
    if (isOnQariRoute || isTeacherView || isStaffViewingStudent) {
      navigate("/dashboard-qari");
      return;
    }
    goToDashboard();
  };

  useEffect(() => {
    if (!deepLinkToken) return;
    setResolvingStudent(true);
    axios
      .get(`${apiURL}/students/by-token/${deepLinkToken}`)
      .then((res) => {
        const studentData = res.data;
        setStudent(res.data);
        setStudentId(res.data.uid);
        setFeesStatus(res.data.feesStatus || "Not Paid");
        if (!studentData.feesStatus) {
          axios
            .get(`${apiURL}/students/by-token/${deepLinkToken}/fees`)
            .then((feesRes) => setFeesStatus(feesRes.data.status || "Not Paid"))
            .catch(() => {});
        }
      })
      .catch((err) => {
        console.error("Failed to resolve deep link", err);
        alert("Invalid or expired link");
      })
      .finally(() => setResolvingStudent(false));
  }, [deepLinkToken, apiURL]);

  useEffect(() => {
    if (deepLinkToken || !studentId || student) return;
    setResolvingStudent(true);
    const token = localStorage.getItem("token");
    axios
      .get(`${apiURL}/users/${studentId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const data = res.data;
        setStudent({ ...data, id: data.uid, uid: data.uid });
      })
      .catch((err) => console.error("Failed to fetch user", err))
      .finally(() => setResolvingStudent(false));
  }, [studentId, deepLinkToken, student, apiURL]);

  useEffect(() => {
    if (!studentId) return;
    const isStaff = role === "qari" || role === "admin" || role === "teacher";
    const isStudentOwn = role === "student" && hifzUser?.userId === studentId;
    if (!isStaff && !isStudentOwn) return;

    const fetchFeesStatus = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${apiURL}/users/${studentId}/fees`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setFeesStatus(res.data?.feesStatus || "Not Paid");
      } catch (err) {
        console.error("Error fetching latest fees status:", err);
        setFeesStatus("Not Paid");
      }
    };

    fetchFeesStatus();
  }, [studentId, role, hifzUser?.userId, apiURL]);

  const fetchProgressData = async (month, year) => {
    if (!studentId && !deepLinkToken) return;
    setLoading(true);

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const defaultDays = Array.from({ length: daysInMonth }, (_, i) => {
      const date = `${year}-${String(month + 1).padStart(2, "0")}-${String(i + 1).padStart(2, "0")}`;
      return { day: i + 1, date, status: null };
    });
    setDays(defaultDays);

    try {
      let res;
      if (deepLinkToken) {
        res = await axios.get(`${apiURL}/students/by-token/${deepLinkToken}/progress`, {
          params: { month: month + 1, year },
        });
      } else {
        const token = localStorage.getItem("token");
        res = await axios.get(`${apiURL}/users/${studentId}/progress`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { month: month + 1, year },
        });
      }

      const progressData = res.data || [];
      const updatedDays = defaultDays.map((d) => {
        const match = progressData.find(
          (p) =>
            p.id === d.date ||
            p.date === d.date ||
            (p.day === d.day && p.month === month + 1 && p.year === year)
        );
        return match ? { ...d, ...match, status: "done" } : d;
      });

      setDays(updatedDays);
    } catch (err) {
      console.error("Error fetching progress", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceSummary = async (month, year) => {
    if (!studentId && !deepLinkToken) return;

    try {
      let res;
      if (deepLinkToken) {
        res = await axios.get(`${apiURL}/students/by-token/${deepLinkToken}/present-days`, {
          params: { month: month + 1, year },
        });
      } else {
        const token = localStorage.getItem("token");
        res = await axios.get(`${apiURL}/users/${studentId}/present-days`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { month: month + 1, year },
        });
      }
      setAttendanceSummary(res.data);
    } catch (err) {
      console.error("Error fetching attendance summary:", err);
    }
  };

  useEffect(() => {
    fetchProgressData(currentMonth, currentYear);
    fetchAttendanceSummary(currentMonth, currentYear);
  }, [studentId, deepLinkToken, currentMonth, currentYear, apiURL]);

  const openModal = (day) => setSelectedDay(day);
  const closeModal = () => setSelectedDay(null);

  useEffect(() => {
    if (!selectedDay) return;
    const onEscape = (e) => {
      if (e.key === "Escape") closeModal();
    };
    document.body.classList.add("hifz-modal-open");
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onEscape);
    return () => {
      document.body.classList.remove("hifz-modal-open");
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onEscape);
    };
  }, [selectedDay]);

  const saveDayData = (updatedDay) => {
    if (role === "student") {
      alert("You can only view your progress.");
      return;
    }

    const payload = { ...updatedDay, month: currentMonth + 1, year: currentYear };

    setDays((prev) =>
      prev.map((d) => (d.day === updatedDay.day ? { ...payload, status: "done" } : d))
    );

    const token = localStorage.getItem("token");
    axios
      .post(`${apiURL}/users/${studentId}/progress`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        closeModal();
        alert("Progress saved!");
      })
      .catch((err) => console.error("Error saving progress", err));
  };

  const handleFeesChange = async (e) => {
    const newStatus = e.target.value;
    setFeesStatus(newStatus);

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${apiURL}/users/${studentId}/fees`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Fees status updated successfully!");
    } catch (err) {
      console.error("Error updating fees", err);
      alert("Failed to update fees");
    }
  };

  const handlePrevMonth = () => {
    const now = new Date();
    const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    const limitMonth = now.getMonth() - 1;
    const limitYear = now.getFullYear();

    if (prevYear < limitYear || (prevYear === limitYear && prevMonth < limitMonth)) {
      alert("You can only view last month's data.");
      return;
    }
    setCurrentMonth(prevMonth);
    setCurrentYear(prevYear);
  };

  const handleNextMonth = () => {
    const now = new Date();
    const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
    const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;

    if (
      nextYear > now.getFullYear() ||
      (nextYear === now.getFullYear() && nextMonth > now.getMonth())
    ) {
      alert("You cannot view future data.");
      return;
    }
    setCurrentMonth(nextMonth);
    setCurrentYear(nextYear);
  };

  const now = new Date();
  const oneMonthBefore = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const viewingDate = new Date(currentYear, currentMonth, 1);
  const showPrevButton = viewingDate > oneMonthBefore;
  const showNextButton = viewingDate < new Date(now.getFullYear(), now.getMonth(), 1);

  const daysWithData = days.filter((d) => d.status).length;

  if (resolvingStudent) {
    return (
      <>
        <NavbarAll />
        <div className="hifz-progress-wrapper">
          <div className="hifz-progress-loading">Loading student…</div>
        </div>
      </>
    );
  }

  if (loading && days.length === 0) {
    return (
      <>
        <NavbarAll />
        <div className="hifz-progress-wrapper">
          <div className="hifz-progress-loading">Loading progress…</div>
        </div>
      </>
    );
  }

  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
  const emptyBoxes = Array.from({ length: firstDayOfMonth });

  return (
    <>
      <NavbarAll />
      <div className="hifz-progress-wrapper">
        <div className="hifz-progress-topbar" role="banner">
          <button
            type="button"
            className="hifz-progress-back-btn"
            onClick={goBack}
            aria-label={backLabel.replace("← ", "Back to ")}
          >
            {backLabel}
          </button>
          <div className="hifz-progress-topbar-meta">
            <div className="hifz-progress-topbar-titles">
              <h1 className="hifz-progress-title">Progress</h1>
              <p className="hifz-progress-subtitle">
                {currentMonthName} {currentYear}
              </p>
            </div>
            <div className="hifz-progress-topbar-actions">
              <div className="hifz-progress-student-chip">
                <div className="hifz-progress-avatar" aria-hidden="true">
                  {getInitial(studentDisplayName)}
                </div>
                <span className="hifz-progress-student-name">{studentDisplayName}</span>
              </div>
              {student && (
                <button
                  type="button"
                  className="hifz-progress-profile-btn"
                  onClick={goToProfile}
                >
                  Profile
                </button>
              )}
            </div>
          </div>
        </div>

        <main className="hifz-progress-main">
          <section className="hifz-progress-stats" aria-label="Summary">
            <div className="hifz-progress-stat-card">
              <span className="hifz-progress-stat-value">
                {attendanceSummary
                  ? `${attendanceSummary.presentDays}/${attendanceSummary.totalDays}`
                  : "—"}
              </span>
              <span className="hifz-progress-stat-label">Days present</span>
            </div>
            <div className="hifz-progress-stat-card hifz-progress-stat-accent">
              <span className="hifz-progress-stat-value">
                {attendanceSummary?.attendancePercentage ?? "—"}
              </span>
              <span className="hifz-progress-stat-label">Attendance</span>
            </div>
            <div className="hifz-progress-stat-card">
              <span className="hifz-progress-stat-value">{daysWithData}</span>
              <span className="hifz-progress-stat-label">Days logged</span>
            </div>
          </section>

          <section className="hifz-progress-fees-card">
            {(isTeacherView && !updatingFees) ? (
              <div className="hifz-progress-fees-row">
                <div>
                  <span className="hifz-progress-fees-label">Fees status</span>
                  <span
                    className={`hifz-progress-fees-badge ${
                      feesStatus === "Paid" ? "paid" : "unpaid"
                    }`}
                  >
                    {feesStatus || "Not Paid"}
                  </span>
                </div>
                <button
                  type="button"
                  className="hifz-progress-fees-btn"
                  onClick={() => setUpdatingFees(true)}
                >
                  Update
                </button>
              </div>
            ) : isTeacherView && updatingFees ? (
              <div className="hifz-progress-fees-row">
                <label className="hifz-progress-fees-label" htmlFor="fees-select">
                  Fees status
                </label>
                <select
                  id="fees-select"
                  className="hifz-progress-fees-select"
                  value={feesStatus}
                  onChange={handleFeesChange}
                >
                  <option value="Paid">Paid</option>
                  <option value="Not Paid">Not Paid</option>
                </select>
                <button
                  type="button"
                  className="hifz-progress-fees-btn secondary"
                  onClick={() => setUpdatingFees(false)}
                >
                  Done
                </button>
              </div>
            ) : (
              <div className="hifz-progress-fees-row">
                <span className="hifz-progress-fees-label">Fees status</span>
                <span
                  className={`hifz-progress-fees-badge ${
                    feesStatus === "Paid" ? "paid" : "unpaid"
                  }`}
                >
                  {feesStatus || "Not Paid"}
                </span>
              </div>
            )}
          </section>

          <section className="hifz-progress-calendar-card">
            <div className="hifz-progress-month-nav">
              {showPrevButton ? (
                <button type="button" className="hifz-progress-month-btn" onClick={handlePrevMonth}>
                  ← Prev
                </button>
              ) : (
                <span />
              )}
              <h2 className="hifz-progress-month-title">
                {currentMonthName} {currentYear}
              </h2>
              {showNextButton ? (
                <button type="button" className="hifz-progress-month-btn" onClick={handleNextMonth}>
                  Next →
                </button>
              ) : (
                <span />
              )}
            </div>

            <div className="hifz-progress-legend">
              <span className="hifz-progress-legend-item">
                <span className="legend-dot legend-done" /> Logged
              </span>
              <span className="hifz-progress-legend-item">
                <span className="legend-dot legend-empty" /> No entry
              </span>
            </div>

            <p className="hifz-progress-tap-hint">Tap a date to view or edit details</p>

            <div className="hifz-calendar">
              <div className="hifz-calendar-weekdays" aria-hidden="true">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                <span key={d} className="hifz-calendar-weekday">
                  {d}
                </span>
              ))}
              </div>
              <div className="hifz-calendar-days">
              {emptyBoxes.map((_, i) => (
                <span key={`empty-${i}`} className="hifz-calendar-day hifz-calendar-day--empty" />
              ))}

              {days.map((d) => (
                <button
                  key={d.date}
                  type="button"
                  className={`hifz-calendar-day ${
                    d.status ? "hifz-calendar-day--logged" : "hifz-calendar-day--empty-day"
                  }`}
                  onClick={() => openModal(d)}
                >
                  {d.day}
                </button>
              ))}
              </div>
            </div>
          </section>

          {student && (
            <div className="hifz-progress-actions">
              <button type="button" className="hifz-progress-action-btn" onClick={goToProfile}>
                {isTeacherView || isAdminView ? "View student profile" : "View profile"}
              </button>
            </div>
          )}

          <footer className="hifz-progress-footer">
            Developed by{" "}
            <a href="https://codevente.com" target="_blank" rel="noopener noreferrer">
              Codevente
            </a>
          </footer>
        </main>
      </div>

      {selectedDay &&
        createPortal(
          <div
            className="hifz-day-modal-overlay"
            onClick={closeModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="day-detail-title"
          >
            <div className="hifz-day-modal" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="hifz-modal-close"
                onClick={closeModal}
                aria-label="Close"
              >
                ×
              </button>
              <DayDetails
                dayData={selectedDay}
                onSave={saveDayData}
                onClose={closeModal}
                readOnly={role === "student"}
              />
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
